/**
 * MyListingsPage
 * ─────────────────────────────────────────────────────────────
 * Central hub for an individual seller to manage their own ads:
 * view, edit, boost (promote), and delete.
 * Route: /my-listings
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowRight, Car, Edit3, Eye, Loader2, Plus, Rocket, Trash2, ImageOff,
  Calendar, MapPin, Tag, QrCode, Copy, CheckCircle2, Undo2,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { DraftsList } from "../components/drafts/DraftsList";

const fmtSar = (v) =>
  Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

export default function MyListingsPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null); // id of vehicle being deleted
  // iter221 — id currently being toggled sold/unsold, so we can show
  // a spinner on the exact button without blocking the rest of the UI.
  const [soldBusy, setSoldBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/vehicles", {
        params: { mine: true, limit: 100 },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load");
      setItems([]);
    }
  }, []);

  // `user === undefined` = auth still bootstrapping (see auth.jsx).
  // `user === null`      = confirmed anonymous → send them to /login.
  // `user === object`    = authenticated → fetch listings.
  useEffect(() => { if (user) load(); }, [user, load]);

  if (user === null) return <Navigate to="/login" replace />;

  // iter221 — flip the "مباع" (sold) flag. Idempotent: the button's
  // label + behaviour swaps based on the current `is_sold` state so
  // the owner can un-mark at any time and the listing returns to
  // exactly its previous state (backend keeps `status` untouched).
  const toggleSold = async (v) => {
    const nextSold = !v.is_sold;
    setSoldBusy(v.id);
    try {
      const { data } = await apiClient.post(`/vehicles/${v.id}/sold`, { sold: nextSold });
      setItems((cur) => (cur || []).map((row) => row.id === v.id ? { ...row, ...data } : row));
      toast.success(
        nextSold
          ? (lang === "ar" ? "تم وضع علامة «مباعة» على الإعلان." : "Listing marked as sold.")
          : (lang === "ar" ? "أُلغيت علامة «مباعة»." : "Sold mark removed."),
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSoldBusy(null); }
  };

  const remove = async (id) => {
    const confirmText = lang === "ar"
      ? "هل تريد فعلاً حذف هذا الإعلان نهائياً؟ لا يمكن التراجع."
      : "Really delete this listing permanently? This cannot be undone.";
    if (!window.confirm(confirmText)) return;
    setBusy(id);
    try {
      await apiClient.delete(`/vehicles/${id}`);
      toast.success(lang === "ar" ? "تم حذف الإعلان" : "Listing deleted");
      setItems((it) => it.filter((v) => v.id !== id));
    } catch (e) {
      toast.error(formatApiError(e) || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10" data-testid="my-listings-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <div className="overline mb-1">
            {lang === "ar" ? "لوحة البائع" : "Seller cockpit"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#0a0a0a]">
            {lang === "ar" ? "إعلاناتي" : "My listings"}
          </h1>
          <p className="text-sm text-[#6b6b6b] mt-2 max-w-xl">
            {lang === "ar"
              ? "تحكّم كامل بإعلاناتك — تعديل السعر، تحديث الصور، تعزيز الإعلان، أو حذفه."
              : "Full control over your ads — edit price, refresh photos, promote or remove."}
          </p>
        </div>
        <Link
          to="/sell"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-bold shadow-md hover:scale-[1.02] transition-transform whitespace-nowrap"
          style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}
          data-testid="my-listings-new-cta"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">{lang === "ar" ? "إعلان جديد" : "New listing"}</span>
        </Link>
      </div>

      <DraftsList lang={lang} />

      {/* States */}
      {items === null && (
        <div className="flex items-center justify-center py-24 text-[#6b6b6b]">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {items && items.length === 0 && (
        <div
          className="rounded-3xl border-2 border-dashed border-[#c9a55c]/50 bg-[#fbf8ee]/50 p-10 sm:p-14 text-center"
          data-testid="my-listings-empty"
        >
          <span
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
            style={{ backgroundColor: "rgba(201,165,92,0.15)", border: "1.5px solid rgba(201,165,92,0.55)" }}
          >
            <Car className="w-8 h-8 text-[#8a6f2e]" />
          </span>
          <h3 className="font-display text-2xl font-semibold text-[#0a0a0a] mb-2">
            {lang === "ar" ? "لا يوجد إعلانات بعد" : "No listings yet"}
          </h3>
          <p className="text-sm text-[#525252] max-w-md mx-auto mb-6">
            {lang === "ar"
              ? "أنشئ إعلانك الأول في أقل من دقيقتين — صور، مواصفات، سعر، ورقم إعلاني رسمي فوراً."
              : "Post your first ad in under two minutes — photos, specs, price, and an official ad number instantly."}
          </p>
          <Link
            to="/sell"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold shadow-lg hover:scale-[1.03] transition-transform"
            style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}
          >
            <Plus className="w-4 h-4" />
            {lang === "ar" ? "أنشئ أول إعلان" : "Post your first ad"}
          </Link>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="my-listings-grid">
          {items.map((v) => (
            <article
              key={v.id}
              className="rounded-2xl overflow-hidden bg-white border border-[#0a0a0a]/10 hover:border-[#c9a55c]/60 transition-colors shadow-sm hover:shadow-md flex flex-col"
              data-testid={`my-listing-${v.id}`}
            >
              {/* Cover */}
              <Link
                to={`/vehicle/${v.id}`}
                className="relative aspect-video bg-[#f5efe1] block overflow-hidden"
              >
                {(v.image || (v.images && v.images[0])) ? (
                  <img
                    src={v.image || v.images[0]}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      // Try next image before showing empty state
                      const imgs = Array.isArray(v.images) ? v.images.filter(Boolean) : [];
                      const idx = Number(e.currentTarget.dataset.idx || 0) + 1;
                      if (idx < imgs.length) {
                        e.currentTarget.dataset.idx = String(idx);
                        e.currentTarget.src = imgs[idx];
                      } else {
                        e.currentTarget.style.display = "none";
                      }
                    }}
                    data-idx="0"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#c9a55c]/50">
                    <ImageOff className="w-10 h-10" />
                  </div>
                )}
                {/* Ad number badge */}
                {v.ad_number && (
                  <span
                    className="absolute top-2 end-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold bg-black/70 text-white backdrop-blur-sm"
                    dir="ltr"
                  >
                    <Tag className="w-3 h-3" /> {v.ad_number}
                  </span>
                )}
                {/* Boost active pill */}
                {v.boost_active && (
                  <span
                    className="absolute top-2 start-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold shadow-md"
                    style={{ background: "linear-gradient(135deg,#f5d478,#c9a55c)", color: "#0a0a0a" }}
                  >
                    <Rocket className="w-3 h-3" /> {lang === "ar" ? "مميّز" : "Boosted"}
                  </span>
                )}
              </Link>

              {/* Body */}
              <div className="p-4 flex flex-col flex-1">
                {/* iter105 — prominent ad-number strip with a tap-to-copy
                    number + inline QR code that opens the print/share
                    page. Was previously only a tiny badge on the image
                    corner ("CAR-00000027 غير واضح" — user report). */}
                {v.ad_number && (
                  <div className="mb-3 flex items-center gap-2 rounded-xl border-2 p-2.5" style={{ borderColor: "#c9a55c", background: "#fffdf7" }}>
                    <QRCodeSVG
                      value={`${window.location.origin}/vehicle/${v.id}?utm_source=qr&utm_medium=qrcode&utm_campaign=share_listing&utm_content=${v.id}`}
                      size={54}
                      bgColor="#fffdf7"
                      fgColor="#0a0a0a"
                      level="M"
                      includeMargin={false}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8a6f2e" }}>
                        {lang === "ar" ? "رقم الإعلان الرسمي" : "Official ad number"}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-mono text-base font-black text-[#0a0a0a] truncate" dir="ltr" data-testid={`ad-number-${v.id}`}>
                          {v.ad_number}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            navigator.clipboard.writeText(v.ad_number);
                            toast.success(lang === "ar" ? "نُسخ" : "Copied");
                          }}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-md hover:bg-[#c9a55c]/20 text-[#8a6f2e]"
                          data-testid={`copy-ad-${v.id}`}
                          aria-label={lang === "ar" ? "نسخ" : "Copy"}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); navigate(`/vehicle/${v.id}`); }}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-md hover:bg-[#c9a55c]/20 text-[#8a6f2e]"
                          data-testid={`qr-open-${v.id}`}
                          aria-label={lang === "ar" ? "افتح رمز QR" : "Open QR"}
                          title={lang === "ar" ? "افتح رمز QR كبير للطباعة أو المشاركة" : "Open large QR for print/share"}
                        >
                          <QrCode className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="text-[10px] text-[#6b6b6b] mt-0.5">
                        {lang === "ar" ? "امسح الرمز أو انسخ الرقم للمشاركة" : "Scan the code or copy the number to share"}
                      </div>
                    </div>
                  </div>
                )}
                <h3 className="font-display text-lg font-semibold text-[#0a0a0a] line-clamp-1">
                  {v.make} {v.model}
                </h3>
                <div className="flex flex-wrap items-center gap-3 text-xs text-[#6b6b6b] mt-1">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {v.year}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {v.city}
                  </span>
                </div>
                <div className="mt-3 text-2xl font-bold text-[#0a0a0a]" dir="ltr">
                  {fmtSar(v.price_sar)} <span className="text-sm font-medium text-[#8a6f2e]">SAR</span>
                </div>

                {/* iter221 — «مباعة» pill: visible ONLY when the owner
                    has toggled the flag on. Anchored right under the
                    price so it never overlaps the ad-number strip,
                    the image, or the action row. */}
                {v.is_sold && (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                       style={{ background: "rgba(15,124,58,0.12)", color: "#0f7c3a", border: "1px solid rgba(15,124,58,0.32)" }}
                       data-testid={`sold-pill-${v.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {lang === "ar" ? "مباعة" : "Sold"}
                  </div>
                )}

                {/* «مباع / إلغاء» owner toggle. One button, label flips
                    based on current state — this is the only new UI
                    surface introduced by iter221. */}
                <button
                  type="button"
                  onClick={() => toggleSold(v)}
                  disabled={soldBusy === v.id}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-bold transition-colors disabled:opacity-60"
                  style={v.is_sold
                    ? { background: "#fff", color: "#0f7c3a", border: "1.5px solid rgba(15,124,58,0.35)" }
                    : { background: "#0a0a0a", color: "#f7eecd" }}
                  data-testid={`toggle-sold-${v.id}`}>
                  {soldBusy === v.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : v.is_sold
                      ? <Undo2 className="w-4 h-4" />
                      : <CheckCircle2 className="w-4 h-4" />}
                  {v.is_sold
                    ? (lang === "ar" ? "إلغاء «مباعة»" : "Undo Sold")
                    : (lang === "ar" ? "وضع علامة «مباعة»" : "Mark as Sold")}
                </button>

                {/* Action row */}
                <div className="grid grid-cols-4 gap-2 mt-4 pt-3 border-t border-[#0a0a0a]/8">
                  <button
                    type="button"
                    onClick={() => navigate(`/vehicle/${v.id}`)}
                    className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-medium text-[#0a0a0a] hover:bg-[#faf6ea] transition-colors"
                    data-testid={`view-${v.id}`}
                  >
                    <Eye className="w-4 h-4 text-[#8a6f2e]" />
                    {lang === "ar" ? "عرض" : "View"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/vehicle/${v.id}/edit`)}
                    className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-medium text-[#0a0a0a] hover:bg-[#faf6ea] transition-colors"
                    data-testid={`edit-${v.id}`}
                  >
                    <Edit3 className="w-4 h-4 text-[#8a6f2e]" />
                    {lang === "ar" ? "تعديل" : "Edit"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/vehicle/${v.id}/boost`)}
                    className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-medium hover:bg-[#faf6ea] transition-colors"
                    style={{ color: v.boost_active ? "#8a6f2e" : "#0a0a0a" }}
                    data-testid={`boost-${v.id}`}
                  >
                    <Rocket className="w-4 h-4" style={{ color: v.boost_active ? "#c9a55c" : "#8a6f2e" }} />
                    {lang === "ar" ? "تعزيز" : "Boost"}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(v.id)}
                    disabled={busy === v.id}
                    className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-medium text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50"
                    data-testid={`delete-${v.id}`}
                  >
                    {busy === v.id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />}
                    {lang === "ar" ? "حذف" : "Delete"}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Footer note */}
      {items && items.length > 0 && (
        <div className="mt-8 text-xs text-[#8a8a8a] text-center">
          {lang === "ar"
            ? `إجمالي إعلاناتك: ${items.length}`
            : `Total listings: ${items.length}`}
          <span className="mx-2">·</span>
          <Link to="/sell" className="text-[#8a6f2e] hover:underline font-semibold inline-flex items-center gap-1">
            {lang === "ar" ? "أضف إعلاناً جديداً" : "Add another ad"} <ArrowRight className="w-3 h-3 rtl:rotate-180" />
          </Link>
        </div>
      )}
    </div>
  );
}
